// Shared constants, types and helpers for the Horarios (staff schedules) screen.

export const DOWS = [
  { key: 0, label: 'Lun' },
  { key: 1, label: 'Mar' },
  { key: 2, label: 'Mié' },
  { key: 3, label: 'Jue' },
  { key: 4, label: 'Vie' },
  { key: 5, label: 'Sáb' },
  { key: 6, label: 'Dom' },
] as const;

export interface TimeRange {
  value: string;
  label: string;
  hours: number;
}

export const TIME_RANGES: readonly TimeRange[] = [
  { value: '09:00-13:00', label: 'Mañana',       hours: 4 },
  { value: '14:00-18:00', label: 'Tarde',        hours: 4 },
  { value: '09:00-18:00', label: 'Día completo', hours: 9 },
  { value: '16:00-21:00', label: 'Tarde-noche',  hours: 5 },
  { value: '10:00-14:00', label: 'Mañana corta', hours: 4 },
];

// Stable per-centre colour palette (dot + light background tint). Indexed by
// the centre's first-appearance order in the week payload.
export interface CentrePalette { dot: string; tint: string }

export const CENTRE_PALETTE: readonly CentrePalette[] = [
  { dot: '#2F7A4F', tint: 'rgba(47, 122, 79, 0.10)' },   // forest green
  { dot: '#C2691C', tint: 'rgba(194, 105, 28, 0.10)' },  // orange
  { dot: '#2563B0', tint: 'rgba(37, 99, 176, 0.10)' },   // blue
  { dot: '#7A4FB0', tint: 'rgba(122, 79, 176, 0.10)' },  // purple
  { dot: '#A1791C', tint: 'rgba(161, 121, 28, 0.10)' },  // gold
];

export const DEFAULT_PALETTE: CentrePalette = { dot: '#6b7280', tint: 'rgba(107, 114, 128, 0.10)' };

export interface CentreMeta { name: string; code: string; palette: CentrePalette }
export interface StaffRow { id: string; name: string; isActive: boolean; centres: { id: string; name: string }[] }
export interface DayCell { centerId: string; timeRange: string }

/** Two-letter centre code from its name, e.g. "Centro Norte" → "CN". */
export function centreCode(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '—';
  if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase();
  const first = words[0] ?? '';
  const last = words[words.length - 1] ?? '';
  return (first[0] + last[0]).toUpperCase();
}

export function isoDate(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function getMondayOf(d: Date): Date {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const mon = new Date(d);
  mon.setDate(d.getDate() + diff);
  mon.setHours(0, 0, 0, 0);
  return mon;
}

export function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setDate(d.getDate() + days);
  return x;
}

export function rangeLabel(monday: Date): string {
  const sunday = addDays(monday, 6);
  const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const m0 = MONTHS[monday.getMonth()] ?? '';
  const m1 = MONTHS[sunday.getMonth()] ?? '';
  return m0 === m1
    ? `${String(monday.getDate())} – ${String(sunday.getDate())} ${m0} ${String(monday.getFullYear())}`
    : `${String(monday.getDate())} ${m0} – ${String(sunday.getDate())} ${m1} ${String(monday.getFullYear())}`;
}

function toMinutes(hhmm: string): number {
  return Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
}

function fmtMinutes(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Hours of a "HH:MM-HH:MM" range — parsed, so derived (non-preset) ranges work too. */
export function rangeHours(value: string): number {
  const [start, end] = value.split('-');
  if (!start || !end) return 0;
  const mins = toMinutes(end) - toMinutes(start);
  return mins > 0 ? Math.round((mins / 60) * 10) / 10 : 0;
}

/**
 * Derive shift presets from a centre's opening window for a day.
 *   - "Día completo" = open–close (always).
 *   - If the window is ≥6h, also "Mañana" (open–mid) and "Tarde" (mid–close),
 *     with `mid` = midpoint rounded to the hour.
 */
export function deriveRanges(open: string, close: string): TimeRange[] {
  const o = toMinutes(open);
  const c = toMinutes(close);
  if (c <= o) return [];
  const full: TimeRange = { value: `${open}-${close}`, label: 'Día completo', hours: (c - o) / 60 };
  if (c - o < 6 * 60) return [full];
  const mid = Math.round((o + c) / 2 / 60) * 60;
  const midStr = fmtMinutes(mid);
  return [
    { value: `${open}-${midStr}`, label: 'Mañana', hours: (mid - o) / 60 },
    { value: `${midStr}-${close}`, label: 'Tarde', hours: (c - mid) / 60 },
    full,
  ];
}

export function downloadCsv(filename: string, rows: readonly (readonly (string | number)[])[]) {
  const csv = rows
    .map((row) => row.map((cell) => {
      const s = String(cell);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(','))
    .join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
